import { 
  Heart, Stethoscope, Pill, Users, Activity, Smile, Shield, Zap, Target, 
  TrendingUp, Globe, Settings, Database, Monitor, Brain, BarChart3, 
  CheckCircle, Lock, Clock, Calendar, Star, Package
} from 'lucide-react';
import { ProductConfigs } from './types';

export const expandedHealthcareSolutionsConfigs: ProductConfigs = {
  // Missing Healthcare Products with Complete 6+6+9 Structure

  'patient-relationship-management': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Comprehensive Patient Engagement',
        description: 'Advanced patient relationship management with personalized communication, engagement tracking, and satisfaction optimization.',
        metrics: '95% patient satisfaction improvement',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Calendar,
        title: 'Intelligent Appointment Scheduling',
        description: 'AI-powered appointment optimization with patient preferences, provider availability, and treatment coordination.',
        metrics: '90% scheduling efficiency improvement',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Heart,
        title: 'Patient Journey Optimization',
        description: 'Comprehensive patient journey mapping with touchpoint optimization and experience personalization.',
        metrics: '200% patient engagement increase',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Star,
        title: 'Patient Loyalty Programs',
        description: 'Advanced loyalty and rewards system with health milestone recognition and engagement incentives.',
        metrics: '85% patient retention rate',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Patient Analytics Dashboard',
        description: 'Comprehensive patient analytics with satisfaction metrics, engagement tracking, and outcome measurement.',
        metrics: 'Real-time insights from 100+ patient KPIs',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Package,
        title: 'Patient Communication Hub',
        description: 'Integrated communication platform with automated reminders, educational content, and feedback collection.',
        metrics: '90% communication effectiveness',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Patient Insights',
        description: 'Machine learning algorithms analyzing patient behavior, preferences, and engagement patterns for optimization.',
        metrics: '90% patient behavior prediction accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Automated Patient Workflows',
        description: 'Intelligent automation of patient communication, follow-ups, and engagement processes.',
        metrics: '70% reduction in administrative tasks',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Database,
        title: 'Patient Data Integration',
        description: 'Comprehensive patient data integration with EHR systems, treatment history, and health outcomes.',
        metrics: 'Complete patient data visibility',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Custom Patient Journeys',
        description: 'Flexible patient journey customisation for different conditions, treatments, and care pathways.',
        metrics: 'Unlimited journey customisation',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Patient Management',
        description: 'Centralized patient management across multiple healthcare facilities with unified patient profiles.',
        metrics: 'Seamless multi-location experience',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Activity,
        title: 'Patient Health Tracking',
        description: 'Comprehensive patient health monitoring with wellness tracking and outcome measurement.',
        metrics: '95% health outcome tracking accuracy',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'fitness-wellness-platform': {
    keyBenefits: [
      {
        icon: Activity,
        title: 'Holistic Fitness Management',
        description: 'Comprehensive fitness and wellness platform with workout tracking, nutrition planning, and health monitoring.',
        metrics: '90% fitness goal achievement rate',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Heart,
        title: 'Personalized Wellness Programs',
        description: 'AI-powered personalized wellness programs with adaptive fitness plans and nutrition recommendations.',
        metrics: '85% program completion rate',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Brain,
        title: 'Mental Health Integration',
        description: 'Integrated mental health support with meditation, stress management, and wellness coaching.',
        metrics: '80% stress reduction improvement',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Community Wellness Network',
        description: 'Social wellness community with challenges, group activities, and peer support systems.',
        metrics: '95% community engagement rate',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Goal Achievement Tracking',
        description: 'Comprehensive goal setting and achievement tracking with milestone recognition and progress visualization.',
        metrics: '90% goal achievement rate',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Wellness Analytics Dashboard',
        description: 'Advanced wellness analytics with health metrics, progress tracking, and improvement insights.',
        metrics: 'Track 50+ wellness indicators',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'Real-Time Health Monitoring',
        description: 'Continuous health monitoring with wearable integration and real-time health alerts.',
        metrics: '24/7 health monitoring',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Wellness Protocols',
        description: 'Flexible wellness protocol customisation for different fitness levels, health conditions, and goals.',
        metrics: 'Unlimited protocol customisation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Health Data Integration',
        description: 'Comprehensive health data integration with medical records, fitness devices, and wellness apps.',
        metrics: 'Complete health data visibility',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'Global Wellness Network',
        description: 'International wellness platform with global challenges, cultural wellness practices, and expert guidance.',
        metrics: 'Connect with wellness experts worldwide',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Calendar,
        title: 'Intelligent Scheduling',
        description: 'AI-powered wellness activity scheduling with lifestyle integration and optimal timing recommendations.',
        metrics: '95% schedule adherence rate',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Star,
        title: 'Gamified Wellness Experience',
        description: 'Engaging gamification with achievements, rewards, and competitive challenges to motivate wellness goals.',
        metrics: '85% user engagement increase',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'dental-practice-management': {
    keyBenefits: [
      {
        icon: Smile,
        title: 'Complete Dental Practice Operations',
        description: 'Comprehensive dental practice management with patient scheduling, treatment planning, and practice analytics.',
        metrics: '90% practice efficiency improvement',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Calendar,
        title: 'Advanced Appointment Management',
        description: 'Intelligent dental appointment scheduling with treatment time optimization and patient preference tracking.',
        metrics: '95% appointment scheduling efficiency',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: Users,
        title: 'Patient Care Coordination',
        description: 'Comprehensive patient care coordination with treatment history, dental records, and care plan management.',
        metrics: '85% patient satisfaction improvement',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Package,
        title: 'Dental Inventory Management',
        description: 'Smart dental supply and equipment management with automated reordering and cost optimization.',
        metrics: '70% inventory cost reduction',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Practice Performance Analytics',
        description: 'Advanced practice analytics with revenue tracking, patient metrics, and operational insights.',
        metrics: 'Track 75+ practice KPIs',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Star,
        title: 'Patient Experience Excellence',
        description: 'Enhanced patient experience with communication tools, education resources, and satisfaction tracking.',
        metrics: '4.8/5 patient satisfaction rating',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Treatment Planning',
        description: 'Intelligent treatment planning with AI-powered diagnosis support and treatment recommendation optimization.',
        metrics: '90% treatment planning accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Monitor,
        title: 'Digital Imaging Integration',
        description: 'Comprehensive digital imaging integration with X-ray management, 3D imaging, and diagnostic tools.',
        metrics: 'Complete imaging integration',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Database,
        title: 'Electronic Dental Records',
        description: 'Complete electronic dental records system with treatment history, imaging, and patient data management.',
        metrics: 'Paperless practice operations',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Custom Treatment Protocols',
        description: 'Flexible treatment protocol customisation for different procedures, specialties, and practice preferences.',
        metrics: 'Unlimited protocol customisation',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Practice Management',
        description: 'Centralized management for multi-location dental practices with unified patient records and operations.',
        metrics: 'Seamless multi-location coordination',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Activity,
        title: 'Practice Automation Suite',
        description: 'Comprehensive practice automation with appointment reminders, follow-ups, and administrative workflows.',
        metrics: '80% administrative task automation',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};